import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./user-nav.css";
import {
  RefreshIconV1,
  MenuIconV1,
  CoinsIconV1,
  KeyIconV1,
  InfoIconV1,
  LogOutIconV1,
  MailIconV1,
  BanIconV1,
} from "images/image-component";
import BrandLogo from "components/brand-logo/BrandLogo";
import RolePill from "components/role-pill/RolePill";
import Button from "components/button/Button";
import SettingsDrawer, {
  DrawerItem,
  DrawerSection,
} from "components/settings-drawer/SettingsDrawer";
import { useRefreshCard } from "pages/user-page/useRefreshCard";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { useTokenSupport } from "features/tokens/useTokenSupport";
import { useAuth } from "auth";
import { useSelector } from "react-redux";
import { cardNumberSelector, userProfileSelector } from "store/userSliceHelpers";
import NotificationEmailModal from "features/notifications/NotificationEmailModal";
import SuspendCardModal from "features/cards/suspend/SuspendCardModal";
import { getNetworkConfig, NETWORK_NAME, NetworkType } from "utils/network";
import { tokenLabel } from "utils/tokens";

interface Props {
  networkType?: NetworkType;
}

function UserNav({ networkType }: Props) {
  const navigate = useNavigate();
  const network = getNetworkConfig(networkType);
  const { isRefreshing, handleRefresh } = useRefreshCard();
  const { signOut } = useAuth();
  const {
    tokens,
    selected: selectedToken,
    label: tokenSymbol,
    param: tokenAddress,
    canSwitch,
    select: selectToken,
  } = useSettlementToken();
  // The portal chose the token; only the deploy knows whether its registry holds it. `undefined`
  // means "not answered yet" and deliberately shows nothing — an unreachable backend is not
  // evidence that a token is unusable.
  const { data: tokenSupport } = useTokenSupport(tokenAddress);
  const tokenIssue =
    tokenSupport?.supported === false
      ? `${tokenSymbol} is not enabled on this network`
      : tokenSupport?.supported && tokenSupport.enabled === false
        ? `${tokenSymbol} is paused for new payments`
        : undefined;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [suspendOpen, setSuspendOpen] = useState(false);
  // The address receipts go to, and the whole preference: absent means nothing is sent.
  const notificationEmail = useSelector(userProfileSelector)?.notificationEmail;
  const cardNumber = useSelector(cardNumberSelector);

  const handleLogout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const tokenDrawerItems: DrawerItem[] = tokens.map((token) => ({
    label: tokenLabel(token),
    icon: <CoinsIconV1 />,
    selected: token.address === selectedToken?.address,
    onClick: () => selectToken(token.address),
  }));

  const settingsSections: DrawerSection[] = [
    {
      label: "Preferences",
      // Absent, not disabled, when the network offers only one token.
      items: canSwitch
        ? [
            {
              label: "Network token",
              icon: <CoinsIconV1 />,
              value: tokenSymbol,
              hint: tokenIssue,
              badge: tokenIssue ? "!" : undefined,
              submenu: {
                title: "Network token",
                sections: [{ items: tokenDrawerItems }],
              },
            },
          ]
        : [],
    },
    {
      label: "Account",
      items: [
        {
          label: "Change password",
          icon: <KeyIconV1 />,
          onClick: () => navigate("/update-password"),
        },
        // One row either way, and the label says which state it is in — tapping it opens the
        // same flow, to turn receipts on or to move them to another address. The drawer closes
        // itself on an action row, so the modal opens against the page, never over the drawer.
        notificationEmail
          ? {
              label: "Email notifications",
              icon: <MailIconV1 />,
              hint: notificationEmail,
              value: "On",
              onClick: () => setNotifyOpen(true),
            }
          : {
              label: "Enable email notifications",
              icon: <MailIconV1 />,
              hint: "Get a receipt after every payment and refund",
              onClick: () => setNotifyOpen(true),
            },
      ],
    },
    {
      label: "Card management",
      // Absent, not disabled, when the account has no card — the drawer drops empty sections.
      items: cardNumber
        ? [
            {
              label: "Suspend card",
              icon: <BanIconV1 />,
              hint: "Stops this card being used for payments",
              variant: "danger",
              onClick: () => setSuspendOpen(true),
            },
          ]
        : [],
    },
    {
      label: "Help and settings",
      items: [
        {
          label: "About",
          icon: <InfoIconV1 />,
          external: true,
          onClick: () => window.open("https://kea-valley.com/", "_blank"),
        },
        {
          label: "Logout",
          icon: <LogOutIconV1 />,
          variant: "danger",
          onClick: handleLogout,
        },
      ],
    },
  ];

  return (
    <>
      <header className="gp-topbar">
        <div className="gp-topbar__brand">
          {/* <BrandLogo />
          <RolePill label="User" variant="pos" /> */}
        </div>

        <div className="gp-nav-actions">
          <button
            className={`refresh_mini-btn${isRefreshing ? " refresh_mini-btn--spinning" : ""}`}
            aria-label="Refresh data"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshIconV1 size={16} />
          </button>
          {tokenSymbol && (
            <span
              className={`gp-token-label${tokenIssue ? " gp-token-label--warn" : ""}`}
              title={tokenIssue ?? `Amounts shown in ${tokenSymbol}`}
            >
              {tokenSymbol}
            </span>
          )}
          <div className={`gp-chip-network gp-chip-network--${network.value}`}>
            {/* Separate span so the narrow-viewport rule can drop the prefix and
                keep just "Mainnet" / "Testnet". */}
            <span className="gp-chip-network__prefix">{NETWORK_NAME}</span>
            {network.shortLabel}
          </div>
          <button
            className="gp-nav-menu-btn"
            aria-label="Settings"
            aria-expanded={settingsOpen}
            aria-haspopup="dialog"
            onClick={() => setSettingsOpen(true)}
          >
            <MenuIconV1 />
          </button>
        </div>
      </header>

      <SettingsDrawer
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        sections={settingsSections}
      />

      {/* A SIBLING of the drawer, not a child: the drawer unmounts on the click that opens this,
          which would take the modal with it. Mounted only while open so the flow hook starts from
          a clean state on every visit. */}
      {notifyOpen && (
        <NotificationEmailModal
          isOpen={notifyOpen}
          onClose={() => setNotifyOpen(false)}
        />
      )}

      {suspendOpen && (
        <SuspendCardModal
          isOpen={suspendOpen}
          onClose={() => setSuspendOpen(false)}
        />
      )}
    </>
  );
}

export default UserNav;
